import { Component, OnChanges, OnDestroy, OnInit } from '@angular/core';
import {
    GolfCourseHoleStat,
    GolfCourseHoleStatKey,
    GolfCourseHoleStatsRound,
    GolfCourseHoleStatsRoundKey,
    GolfCourseModel,
    ScheduledEntry
} from 'src/app/_interfaces';
import { GolfTournament } from 'src/app/_interfaces/golf/golf-tournament';
import { AppStringsService } from 'src/app/_services';
import { ScheduledEntryStateService } from 'src/app/tournament-section/tournament/scheduled-entry-state.service';
import { SponsorBannerComponent } from '../../sponsor-banner/sponsor-banner.component';
import { NgStyle } from '@angular/common';
import { AppStringComponent } from '../../app-string/app-string.component';
import { WeatherComponent } from '../../weather/weather.component';
import { Subscription } from 'rxjs';
import { SsrlocalstoreService } from 'src/app/_services/ssr-localstore.service';

type CourseViewTab = 'overview' | 'stats';
type CourseStatsRoundOption = { key: GolfCourseHoleStatsRoundKey };
type CourseStatsRow = {
    key: GolfCourseHoleStatKey;
    values: Array<number | string | null>;
};

@Component({
    selector: 'app-course-details-card',
    templateUrl: './course-details-card.component.html',
    styleUrls: ['./course-details-card.component.scss'],
    imports: [SponsorBannerComponent, AppStringComponent, WeatherComponent, NgStyle]
})
export class CourseDetailsCardComponent implements OnInit, OnChanges, OnDestroy {
    entry!: ScheduledEntry;
    golfTour: GolfTournament | null = null;

    expanded: boolean[] = []
    private expandedByCourseId = new Map<string, boolean>();
    private selectedTabByCourseId = new Map<string, CourseViewTab>();
    private selectedStatsRoundByCourseId = new Map<string, GolfCourseHoleStatsRoundKey>();

    public isMetric = false;

    private subs = new Subscription();
    private readonly COURSE_STATS_ORDER: GolfCourseHoleStatKey[] = [
      'avg_score',
      'avg_to_par',
      'ace',
      'double_eagle',
      'eagle',
      'birdie',
      'par',
      'bogey',
      'double_bogey',
      'triple_bogey',
      'other',
    ];
    private readonly STAT_LABELS: Record<GolfCourseHoleStatKey, { key: string; fallback: string }> = {
      avg_score: { key: 'golfstats.avg_score', fallback: 'AVG' },
      avg_to_par: { key: 'golfstats.avg_to_par', fallback: '+/-' },
      ace: { key: 'golfstats.ace', fallback: 'Hole in One' },
      double_eagle: { key: 'golfstats.eagle.double', fallback: 'Albatross' },
      eagle: { key: 'golfstats.eagle', fallback: 'Eagle' },
      birdie: { key: 'golfstats.birdie', fallback: 'Birdie' },
      par: { key: 'golfstats.par', fallback: 'Par' },
      bogey: { key: 'golfstats.bogey', fallback: 'Bogey' },
      double_bogey: { key: 'golfstats.bogey.double', fallback: 'Double Bogey' },
      triple_bogey: { key: 'golfstats.bogey.triple', fallback: 'Triple Bogey' },
      other: { key: 'golfstats.other', fallback: 'Other' },
    };
    private readonly STAT_SHORT_LABELS: Partial<Record<GolfCourseHoleStatKey, { key: string; fallback: string }>> = {
      ace: { key: 'golfstats.ace.short', fallback: 'ACE' },
      double_eagle: { key: 'golfstats.eagle.double.short', fallback: 'ALB' },
      eagle: { key: 'golfstats.eagle.short', fallback: 'EAG' },
      birdie: { key: 'golfstats.birdie.short', fallback: 'BRD' },
      bogey: { key: 'golfstats.bogey.short', fallback: 'BOG' },
      double_bogey: { key: 'golfstats.bogey.double.short', fallback: 'DB' },
      triple_bogey: { key: 'golfstats.bogey.triple.short', fallback: 'TB' },
    };

    constructor(
        private entryState: ScheduledEntryStateService,
        public ssr: SsrlocalstoreService,
        private appStrings: AppStringsService
    ) { }

    get hasPurse(): boolean {
      return this.golfTour?.purse !== null && this.golfTour?.purse !== undefined;
    }

    ngOnInit(): void {
        this.isMetric = this.resolveIsMetric();

        this.subs.add(
          this.entryState.entry.subscribe((data) => {
            this.entry = data;
          })
        );

        this.subs.add(
          this.entryState.tournament.subscribe((data) => {
            this.golfTour = data;
            this.syncExpanded();
          })
        );
    }

    ngOnChanges(): void {
      this.syncExpanded();
    }

    ngOnDestroy(): void {
      this.subs.unsubscribe();
    }

    private syncExpanded(): void {
      const courses = this.golfTour?.courses ?? [];
      const validCourseIds = new Set(courses.map((course) => this.getCourseId(course)));

      Array.from(this.expandedByCourseId.keys()).forEach((courseId) => {
        if (!validCourseIds.has(courseId)) {
          this.expandedByCourseId.delete(courseId);
        }
      });

      Array.from(this.selectedTabByCourseId.keys()).forEach((courseId) => {
        if (!validCourseIds.has(courseId)) {
          this.selectedTabByCourseId.delete(courseId);
        }
      });

      Array.from(this.selectedStatsRoundByCourseId.keys()).forEach((courseId) => {
        if (!validCourseIds.has(courseId)) {
          this.selectedStatsRoundByCourseId.delete(courseId);
        }
      });

      courses.forEach((course) => {
        const courseId = this.getCourseId(course);
        const options = this.getStatsRoundOptions(course);

        if (!options.length) {
          this.selectedTabByCourseId.set(courseId, 'overview');
          this.selectedStatsRoundByCourseId.delete(courseId);
          return;
        }

        const selectedRound = this.selectedStatsRoundByCourseId.get(courseId);
        if (!selectedRound || !options.some((option) => option.key === selectedRound)) {
          this.selectedStatsRoundByCourseId.set(courseId, options[0].key);
        }
      });

      this.expanded = courses.map((course) => {
        const courseId = this.getCourseId(course);
        const hasHoles = this.hasHoles(course);
        const expanded = this.expandedByCourseId.has(courseId)
          ? !!this.expandedByCourseId.get(courseId) && hasHoles
          : hasHoles;

        this.expandedByCourseId.set(courseId, expanded);
        return expanded;
      });
    }

    hasHoles(course: GolfCourseModel): boolean {
        return Array.isArray(course?.holes) && course.holes.length > 0;
    }

    canShowStatsTab(course: GolfCourseModel): boolean {
      return this.hasHoles(course) && this.getStatsRoundOptions(course).length > 0;
    }

    getSelectedTab(course: GolfCourseModel): CourseViewTab {
      return this.selectedTabByCourseId.get(this.getCourseId(course)) ?? 'overview';
    }

    setSelectedTab(course: GolfCourseModel, tab: CourseViewTab): void {
      if (tab === 'stats' && !this.canShowStatsTab(course)) return;
      this.selectedTabByCourseId.set(this.getCourseId(course), tab);
    }

    getStatsRoundOptions(course: GolfCourseModel): CourseStatsRoundOption[] {
      if (!this.hasHoles(course)) return [];

      const holeStats = course?.hole_stats;
      if (!holeStats || typeof holeStats !== 'object') return [];

      const options: CourseStatsRoundOption[] = [];

      if (this.roundHasStats(holeStats.total)) {
        options.push({ key: 'total' });
      }

      const availableRounds = this.getAvailableStatsRoundKeys(holeStats);
      const tournamentRounds = this.getTournamentRoundKeys();
      const orderedRounds = tournamentRounds.length
        ? tournamentRounds.filter((key) => availableRounds.includes(key))
        : availableRounds;

      orderedRounds.forEach((key) => options.push({ key }));

      return options;
    }

    getSelectedStatsRound(course: GolfCourseModel): GolfCourseHoleStatsRoundKey | null {
      const options = this.getStatsRoundOptions(course);
      if (!options.length) return null;

      const selectedRound = this.selectedStatsRoundByCourseId.get(this.getCourseId(course));
      return options.some((option) => option.key === selectedRound) ? selectedRound ?? options[0].key : options[0].key;
    }

    onStatsRoundChange(course: GolfCourseModel, event: Event): void {
      const selectedRound = (event.target as HTMLSelectElement | null)?.value as GolfCourseHoleStatsRoundKey | undefined;
      if (!selectedRound) return;
      this.selectedStatsRoundByCourseId.set(this.getCourseId(course), selectedRound);
    }

    getStatsRoundLabel(roundKey: GolfCourseHoleStatsRoundKey): string {
      if (roundKey === 'total') {
        return this.translate('golf.rounds.all', 'All Rounds');
      }

      const roundNumber = this.getRoundNumber(roundKey);
      if (!roundNumber) return roundKey;

      switch (roundNumber) {
        case 1:
          return this.translate('golf.rounds.one', 'Round 1');
        case 2:
          return this.translate('golf.rounds.two', 'Round 2');
        case 3:
          return this.translate('golf.rounds.three', 'Round 3');
        case 4:
          return this.translate('golf.rounds.four', 'Round 4');
        default:
          return `Round ${roundNumber}`;
      }
    }

    getStatsRows(course: GolfCourseModel): CourseStatsRow[] {
      const selectedRound = this.getSelectedStatsRound(course);
      if (!selectedRound || !Array.isArray(course?.holes) || !course.holes.length) return [];

      const roundStats = course.hole_stats?.[selectedRound] ?? {};

      return this.COURSE_STATS_ORDER.map((key) => ({
        key,
        values: course.holes!.map((hole) => {
          const holeStat = roundStats[String(hole.hole)];
          return this.getHoleStatValue(holeStat, key);
        })
      }));
    }

    getStatsLabel(key: GolfCourseHoleStatKey, short = false): string {
      const shortLabel = short ? this.STAT_SHORT_LABELS[key] : null;
      if (shortLabel) {
        return this.translate(shortLabel.key, shortLabel.fallback);
      }

      const fullLabel = this.STAT_LABELS[key];
      return this.translate(fullLabel.key, fullLabel.fallback);
    }

    displayCourseStat(value: number | string | null): string {
      if (typeof value === 'number') return String(value);
      if (typeof value === 'string' && value.trim().length) return value;
      return '-';
    }

    getCourseStatClass(key: GolfCourseHoleStatKey, value: number | string | null): string {
      if (key !== 'avg_to_par' || typeof value !== 'string') return '';

      const numericValue = Number(value);
      if (Number.isNaN(numericValue)) return '';
      if (numericValue < 0) return 'stat-negative';
      if (numericValue > 0) return 'stat-positive';
      return '';
    }

    getTabLabel(tab: CourseViewTab): string {
      return tab === 'overview'
        ? this.translate('golf.course.tab.overview', 'Overview')
        : this.translate('golf.course.tab.stats', 'Stats');
    }

    toggle(event: Event, idx: number, course: GolfCourseModel): void {
        event.stopPropagation();
        if (!this.hasHoles(course)) return;
        this.expanded[idx] = !this.expanded[idx];
        this.expandedByCourseId.set(this.getCourseId(course), this.expanded[idx]);
    }

    collapse(idx: number): void {
        if (this.expanded[idx]) {
            this.expanded[idx] = false;
        }
    }

    private resolveIsMetric(): boolean {
      if (!this.ssr.isClientSide) return false;

      try {
        const userLocale = navigator.language || navigator.languages?.[0] || 'en-US';
        const region = new Intl.Locale(userLocale).region;
        const imperialCountries = ['US', 'GB', 'CA', 'LR', 'MM'];
        return !imperialCountries.includes(region || '');
      } catch {
        return false;
      }
    }

    formatCourseAbbreviation(abbr?: string | null): string {
      const normalized = abbr?.trim().slice(0, 2).toUpperCase();
      return normalized ? `(${normalized})` : '';
    }

    private getCourseId(course: GolfCourseModel): string {
      return course.id || course.enet_venue_id || course.name;
    }

    private getTournamentRoundKeys(): GolfCourseHoleStatsRoundKey[] {
      const totalRounds = this.golfTour?.events?.find((event) => event.type === 'stroke_play')?.number_of_rounds ?? 0;
      return Array.from({ length: totalRounds }, (_, idx) => `round_${idx + 1}` as GolfCourseHoleStatsRoundKey);
    }

    private getAvailableStatsRoundKeys(holeStats: GolfCourseModel['hole_stats']): GolfCourseHoleStatsRoundKey[] {
      if (!holeStats) return [];

      return Object.keys(holeStats)
        .filter((key): key is GolfCourseHoleStatsRoundKey => key !== 'total' && this.roundHasStats(holeStats[key]))
        .sort((left, right) => (this.getRoundNumber(left) ?? 0) - (this.getRoundNumber(right) ?? 0));
    }

    private getRoundNumber(roundKey: GolfCourseHoleStatsRoundKey): number | null {
      const match = String(roundKey).match(/^round_(\d+)$/);
      return match ? Number(match[1]) : null;
    }

    private roundHasStats(roundStats?: GolfCourseHoleStatsRound | null): boolean {
      if (!roundStats || typeof roundStats !== 'object') return false;
      return Object.values(roundStats).some((holeStat) => this.holeStatsHasValues(holeStat));
    }

    private holeStatsHasValues(holeStat?: GolfCourseHoleStat | null): boolean {
      if (!holeStat || typeof holeStat !== 'object') return false;
      return Object.values(holeStat).some((value) => this.hasDisplayableStatValue(value));
    }

    private hasDisplayableStatValue(value: number | string | null | undefined): boolean {
      return typeof value === 'number' || (typeof value === 'string' && value.trim().length > 0);
    }

    private getHoleStatValue(holeStat: GolfCourseHoleStat | undefined, key: GolfCourseHoleStatKey): number | string | null {
      const value = holeStat?.[key];
      return this.hasDisplayableStatValue(value) ? (value as number | string) : null;
    }

    private translate(key: string, fallback: string): string {
      const value = this.appStrings.getAppString(key);
      return value === key ? fallback : value;
    }
}
